import React, { useState, useRef, useCallback, useEffect } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import {
  Box, Typography, Button, IconButton, Paper, CircularProgress,
  Alert, Snackbar, Chip, Tooltip, Divider,
} from '@mui/material';
import {
  Draw as SignatureIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Send as SendIcon,
  ArrowBack as BackIcon,
  Add as AddIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  DragIndicator as DragIcon,
} from '@mui/icons-material';
import api from '../api';
import { API_BASE_URL } from '../constants';

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.js`;

/** Turn API-relative paths into absolute URLs for fetch/Bearer flows. */
function resolvePdfAssetUrl(input) {
  if (!input || typeof input !== 'string') return '';
  const t = input.trim();
  if (/^https?:\/\//i.test(t)) return t;
  const base = API_BASE_URL.replace(/\/$/, '');
  const path = t.startsWith('/') ? t : `/${t}`;
  return `${base}${path}`;
}

/** Load PDF bytes with JWT (axios) when URL targets the API host; avoids pdf.js fetch without Authorization. */
async function fetchPdfArrayBuffer(pdfUrl) {
  const resolved = resolvePdfAssetUrl(pdfUrl);
  if (!resolved) throw new Error('Missing PDF URL');
  const base = API_BASE_URL.replace(/\/$/, '');
  if (resolved.startsWith(base)) {
    const path = resolved.slice(base.length) || '/';
    const { data } = await api.get(path, { responseType: 'arraybuffer' });
    return data;
  }
  const token = localStorage.getItem('access_token');
  const headers = { ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  const res = await fetch(resolved, { credentials: 'include', headers });
  if (!res.ok) throw new Error(`Failed to load PDF (${res.status})`);
  return res.arrayBuffer();
}

const FIELD_DEFAULT_W = 200;
const FIELD_DEFAULT_H = 50;
const MIN_FIELD_W = 100;
const MIN_FIELD_H = 30;

const ESignaturePrepare = ({ documentId, documentName, pdfUrl, onBack, onSent }) => {
  const [numPages, setNumPages] = useState(0);
  const [pageDimensions, setPageDimensions] = useState({});
  const [fields, setFields] = useState([]);
  const [placingMode, setPlacingMode] = useState(false);
  const [scale, setScale] = useState(1.0);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [error, setError] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [resizing, setResizing] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const canvasRefs = useRef({});
  const pageContainerRefs = useRef({});
  const pdfDocRef = useRef(null);
  const renderRunIdRef = useRef(0);
  const pageRenderTaskRef = useRef({});

  // Load PDF using pdfjs-dist directly
  useEffect(() => {
    if (!pdfUrl) {
      setError('No PDF is attached to this e-signature document. Try creating it again from a file.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);

    const loadPdf = async () => {
      try {
        const bytes = await fetchPdfArrayBuffer(pdfUrl);
        const doc = await pdfjsLib.getDocument({ data: bytes }).promise;
        if (cancelled) return;
        pdfDocRef.current = doc;
        setNumPages(doc.numPages);

        const dims = {};
        for (let i = 1; i <= doc.numPages; i++) {
          const page = await doc.getPage(i);
          const viewport = page.getViewport({ scale: 1.0 });
          dims[i] = { width: viewport.width, height: viewport.height };
        }
        if (!cancelled) {
          setPageDimensions(dims);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(`Failed to load PDF: ${err.message}`);
          setLoading(false);
        }
      }
    };
    loadPdf();
    return () => { cancelled = true; };
  }, [pdfUrl]);

  // Render pages to canvas — avoid overlapping page.render on the same canvas
  useEffect(() => {
    if (!pdfDocRef.current || numPages === 0) return;

    const doc = pdfDocRef.current;
    const runId = ++renderRunIdRef.current;

    const cancelAllPageTasks = () => {
      Object.keys(pageRenderTaskRef.current).forEach((key) => {
        const task = pageRenderTaskRef.current[key];
        try {
          task?.cancel?.();
        } catch (_) {
          /* ignore */
        }
        delete pageRenderTaskRef.current[key];
      });
    };

    const renderPages = async () => {
      await new Promise((resolve) => {
        requestAnimationFrame(() => {
          requestAnimationFrame(resolve);
        });
      });

      for (let i = 1; i <= numPages; i++) {
        if (runId !== renderRunIdRef.current) return;

        const canvas = canvasRefs.current[i];
        if (!canvas) continue;

        const prevTask = pageRenderTaskRef.current[i];
        if (prevTask) {
          try {
            prevTask.cancel();
          } catch (_) {
            /* ignore */
          }
          delete pageRenderTaskRef.current[i];
        }

        let page;
        try {
          page = await doc.getPage(i);
        } catch {
          return;
        }
        if (runId !== renderRunIdRef.current) return;

        const viewport = page.getViewport({ scale });
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext('2d', { alpha: false });
        const task = page.render({ canvasContext: ctx, viewport });
        pageRenderTaskRef.current[i] = task;

        try {
          await task.promise;
        } catch (err) {
          if (runId !== renderRunIdRef.current) return;
          const name = err?.name || '';
          const msg = String(err?.message || '');
          if (
            name === 'RenderingCancelledException'
            || msg.includes('RenderingCancelled')
            || msg.includes('cancelled')
          ) {
            continue;
          }
          throw err;
        }

        if (pageRenderTaskRef.current[i] === task) {
          delete pageRenderTaskRef.current[i];
        }
      }
    };

    renderPages();

    return () => {
      renderRunIdRef.current += 1;
      cancelAllPageTasks();
    };
  }, [numPages, scale]);

  const handlePageClick = useCallback((e, pageNumber) => {
    if (!placingMode || dragging || resizing) return;
    const container = pageContainerRefs.current[pageNumber];
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const x = (e.clientX - rect.left) / scale;
    const y = (e.clientY - rect.top) / scale;
    setFields((prev) => {
      const nextIndex = prev.length + 1;
      const newField = {
        id: Date.now(),
        page: pageNumber,
        x: Math.max(0, x - FIELD_DEFAULT_W / 2),
        y: Math.max(0, y - FIELD_DEFAULT_H / 2),
        width: FIELD_DEFAULT_W,
        height: FIELD_DEFAULT_H,
        label: `Signature ${nextIndex}`,
      };
      return [...prev, newField];
    });
    /* Keep placing mode on so multiple fields can be added without re-clicking the button. */
  }, [placingMode, scale, dragging, resizing]);

  const handleDeleteField = useCallback((fieldId) => {
    setFields((prev) => prev.filter((f) => f.id !== fieldId));
  }, []);

  const handleDragStart = useCallback((e, field) => {
    e.stopPropagation();
    e.preventDefault();
    const container = pageContainerRefs.current[field.page];
    if (!container) return;
    const rect = container.getBoundingClientRect();
    setDragOffset({
      x: (e.clientX - rect.left) / scale - field.x,
      y: (e.clientY - rect.top) / scale - field.y,
    });
    setDragging(field.id);
  }, [scale]);

  const handleResizeStart = useCallback((e, fieldId) => {
    e.stopPropagation();
    e.preventDefault();
    setResizing(fieldId);
  }, []);

  useEffect(() => {
    if (!dragging && !resizing) return;
    const handleMouseMove = (e) => {
      if (dragging) {
        const field = fields.find((f) => f.id === dragging);
        if (!field) return;
        const container = pageContainerRefs.current[field.page];
        if (!container) return;
        const rect = container.getBoundingClientRect();
        const newX = (e.clientX - rect.left) / scale - dragOffset.x;
        const newY = (e.clientY - rect.top) / scale - dragOffset.y;
        setFields((prev) =>
          prev.map((f) =>
            f.id === dragging ? { ...f, x: Math.max(0, newX), y: Math.max(0, newY) } : f
          )
        );
      }
      if (resizing) {
        const field = fields.find((f) => f.id === resizing);
        if (!field) return;
        const container = pageContainerRefs.current[field.page];
        if (!container) return;
        const rect = container.getBoundingClientRect();
        const mouseX = (e.clientX - rect.left) / scale;
        const mouseY = (e.clientY - rect.top) / scale;
        const newW = Math.max(MIN_FIELD_W, mouseX - field.x);
        const newH = Math.max(MIN_FIELD_H, mouseY - field.y);
        setFields((prev) =>
          prev.map((f) =>
            f.id === resizing ? { ...f, width: newW, height: newH } : f
          )
        );
      }
    };
    const handleMouseUp = () => {
      setDragging(null);
      setResizing(null);
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging, resizing, fields, scale, dragOffset]);

  const handleSaveFields = async () => {
    if (fields.length === 0) {
      setSnackbar({ open: true, message: 'Add at least one signature field', severity: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const payload = fields.map(({ id, ...rest }) => rest);
      await api.post(`/api/esignatures/${documentId}/prepare/`, { fields: payload });
      setSnackbar({ open: true, message: 'Fields saved successfully', severity: 'success' });
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.detail || 'Failed to save fields';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSendToClient = async () => {
    if (fields.length === 0) {
      setSnackbar({ open: true, message: 'Place signature fields before sending', severity: 'warning' });
      return;
    }
    setSending(true);
    try {
      const payload = fields.map(({ id, ...rest }) => rest);
      await api.post(`/api/esignatures/${documentId}/prepare/`, { fields: payload });
      await api.post(`/api/esignatures/${documentId}/send/`);
      setSnackbar({ open: true, message: 'Document sent to client for signing', severity: 'success' });
      if (onSent) setTimeout(onSent, 1500);
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.detail || 'Failed to send document';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setSending(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={onBack} aria-label="Go back">
          <BackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={600}>
            Place Signature Fields
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {documentName || `Document #${documentId}`}
          </Typography>
        </Box>
        <Chip
          label={`${fields.length} field${fields.length !== 1 ? 's' : ''}`}
          color={fields.length > 0 ? 'primary' : 'default'}
          size="small"
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      <Paper
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1.5,
          mb: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        <Button
          variant={placingMode ? 'contained' : 'outlined'}
          startIcon={<AddIcon />}
          onClick={() => setPlacingMode(!placingMode)}
          size="small"
        >
          {placingMode ? 'Click on PDF to place' : 'Add Signature Field'}
        </Button>
        <Divider orientation="vertical" flexItem />
        <Tooltip title="Zoom in">
          <IconButton size="small" onClick={() => setScale((s) => Math.min(2, s + 0.1))} aria-label="Zoom in">
            <ZoomInIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Typography variant="caption" color="text.secondary">
          {Math.round(scale * 100)}%
        </Typography>
        <Tooltip title="Zoom out">
          <IconButton size="small" onClick={() => setScale((s) => Math.max(0.5, s - 0.1))} aria-label="Zoom out">
            <ZoomOutIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="outlined"
          startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
          onClick={handleSaveFields}
          disabled={saving || sending || fields.length === 0}
          size="small"
        >
          Save Fields
        </Button>
        <Button
          variant="contained"
          startIcon={sending ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <SendIcon />}
          onClick={handleSendToClient}
          disabled={saving || sending || fields.length === 0}
          size="small"
        >
          Send to Client
        </Button>
      </Paper>

      {placingMode && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Click on the PDF to add signature fields (you can add several). Click &quot;Add Signature Field&quot; again to turn off placement mode.
        </Alert>
      )}

      <Paper
        sx={{
          maxHeight: 'calc(100vh - 280px)',
          overflow: 'auto',
          bgcolor: 'grey.100',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          cursor: placingMode ? 'crosshair' : 'default',
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          Array.from({ length: numPages }, (_, i) => i + 1).map((pageNum) => {
            const dim = pageDimensions[pageNum] || { width: 612, height: 792 };
            return (
              <Box
                key={pageNum}
                sx={{ mb: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'flex-start', mb: 0.5, pl: 0.5 }}>
                  Page {pageNum}
                </Typography>
                <Box
                  ref={(el) => { pageContainerRefs.current[pageNum] = el; }}
                  sx={{
                    position: 'relative',
                    display: 'inline-block',
                    width: dim.width * scale,
                    height: dim.height * scale,
                  }}
                  onClick={(e) => handlePageClick(e, pageNum)}
                >
                  <canvas
                    ref={(el) => { canvasRefs.current[pageNum] = el; }}
                    style={{ display: 'block', width: '100%', height: '100%' }}
                  />
                  {fields
                    .filter((f) => f.page === pageNum)
                    .map((field) => (
                      <Box
                        key={field.id}
                        sx={{
                          position: 'absolute',
                          left: field.x * scale,
                          top: field.y * scale,
                          width: field.width * scale,
                          height: field.height * scale,
                          border: '2px dashed',
                          borderColor: 'primary.main',
                          bgcolor: 'rgba(25, 118, 210, 0.08)',
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: dragging === field.id ? 'grabbing' : 'grab',
                          userSelect: 'none',
                          zIndex: 1,
                          '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.15)' },
                        }}
                        onMouseDown={(e) => handleDragStart(e, field)}
                      >
                        <DragIcon
                          sx={{
                            fontSize: 14 * scale,
                            color: 'primary.main',
                            position: 'absolute',
                            top: 2,
                            left: 2,
                            opacity: 0.6,
                          }}
                        />
                        <SignatureIcon
                          sx={{ fontSize: 16 * scale, color: 'primary.main', mr: 0.5 }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'primary.main',
                            fontWeight: 600,
                            fontSize: 11 * scale,
                          }}
                        >
                          {field.label}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteField(field.id);
                          }}
                          aria-label={`Delete ${field.label}`}
                          sx={{
                            position: 'absolute',
                            top: -8 * scale,
                            right: -8 * scale,
                            bgcolor: 'error.main',
                            color: 'common.white',
                            width: 20 * scale,
                            height: 20 * scale,
                            '&:hover': { bgcolor: 'error.dark' },
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: 12 * scale }} />
                        </IconButton>
                        <Box
                          onMouseDown={(e) => handleResizeStart(e, field.id)}
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            right: 0,
                            width: 12 * scale,
                            height: 12 * scale,
                            cursor: 'nwse-resize',
                            bgcolor: 'primary.main',
                            borderRadius: '2px 0 4px 0',
                          }}
                        />
                      </Box>
                    ))}
                </Box>
              </Box>
            );
          })
        )}
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ESignaturePrepare;
